import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { EditPetProfileContent } from './content'
import { EditPetProfileControls } from './controls'
import { useFileUpload } from '$hooks/use-file-upload'

export enum Input {
  Name = 'name',
  Image = 'image',
}

export const editPetProfileSchema = z.object({
  [Input.Name]: z.string()
    .min(1, { message: 'Name is required.' })
    .max(255, { message: 'Name must be less than 255 characters.' }),
  [Input.Image]: z.instanceof(File).optional(),
})

export type EditPetProfileSchema = z.infer<typeof editPetProfileSchema>

type EditPetProfileFormContextType = {
  onSubmit: (values: EditPetProfileSchema) => void
  onCancel: () => void
  isLoading?: boolean,
  fileHandler: ReturnType<typeof useFileUpload>
}

const EditPetProfileFormContext = createContext<EditPetProfileFormContextType | undefined>(undefined)

type EditPetProfileFormProps = EditPetProfileFormContextType & {
  children?: ReactNode,
  defaultValues?: Partial<EditPetProfileSchema>,
}

export function EditPetProfileForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
  fileHandler,
}: EditPetProfileFormProps) {

  const context: EditPetProfileFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
    fileHandler,
  }

  return (
    <EditPetProfileFormContext.Provider value={context}>
      <Form<EditPetProfileSchema, typeof editPetProfileSchema>
        schema={editPetProfileSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditPetProfileFormContext.Provider>
  )
}

EditPetProfileForm.Content = EditPetProfileContent
EditPetProfileForm.Controls = EditPetProfileControls

export function useEditPetProfileForm() {
  const context = useContext(EditPetProfileFormContext)

  if (context === undefined) {
    throw new Error('useEditPetProfileForm must be used within an EditPetProfileFormProvider')
  }

  return context
}
